"use client";

import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import HangmanFigure from "@/components/tools/games-organizers/hangman-figure";
import type { HANGMAN_GAME_STRINGS } from "@/components/tools/hangman-game";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Round,
  type Slot,
  slots,
  wrongLetters,
} from "@/lib/tool-kit/games-organizers/hangman";

type Strings = typeof HANGMAN_GAME_STRINGS;

function keyLabel(strings: Strings, letter: string, round: Round): string {
  if (!round.guessed.includes(letter)) {
    return letter;
  }
  const state = round.answer.includes(letter)
    ? strings.keyRight
    : strings.keyWrong;
  return t(state, { letter });
}

function keyTone(round: Round, letter: string): string {
  if (!round.guessed.includes(letter)) {
    return "border-border bg-card text-foreground hover:bg-muted";
  }
  return round.answer.includes(letter)
    ? "border-primary bg-primary/15 text-foreground"
    : "border-destructive/40 bg-destructive/10 text-muted-foreground line-through";
}

function slotText(slot: Slot): string {
  if (!slot.shown) {
    return "";
  }
  return slot.char === " " ? "\u00a0" : slot.char;
}

/** One word: the figure, the blanks, the keyboard and the round's controls. */
export default function HangmanRound({
  round,
  keys,
  message,
  isLast,
  canReveal,
  onGuess,
  onClue,
  onReveal,
  onNext,
  strings,
}: {
  round: Round;
  keys: readonly string[];
  message: string;
  isLast: boolean;
  canReveal: boolean;
  onGuess: (letter: string) => void;
  onClue: () => void;
  onReveal: () => void;
  onNext: () => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const left = round.maxMisses - round.misses;
  const wrong = wrongLetters(round);
  const over = round.status !== "playing";
  const figureLabel = t(strings.figureLabel, {
    misses: fmt(round.misses),
    max: fmt(round.maxMisses),
  });
  return (
    <div className="space-y-6">
      <div className="grid items-center gap-6 md:grid-cols-[minmax(0,16rem)_1fr]">
        <HangmanFigure
          label={figureLabel}
          maxMisses={round.maxMisses}
          misses={round.misses}
        />
        <div className="space-y-4">
          <p
            aria-label={slots(round)
              .map((slot) => (slot.shown ? slot.char : strings.blank))
              .join(" ")}
            className="flex flex-wrap justify-center gap-x-2 gap-y-3 md:justify-start"
            role="img"
          >
            {slots(round).map((slot, i) => (
              <span
                aria-hidden="true"
                className={`flex h-12 min-w-[2.25rem] items-end justify-center font-bold text-4xl sm:h-16 sm:min-w-[3rem] sm:text-5xl ${slot.isLetter ? "border-foreground border-b-4" : ""} ${round.status === "lost" && slot.isLetter && !round.guessed.includes(slot.char) ? "text-destructive" : "text-foreground"}`}
                // biome-ignore lint/suspicious/noArrayIndexKey: slots are positional and repeat
                key={i}
              >
                {slotText(slot)}
              </span>
            ))}
          </p>
          <p className="font-medium text-foreground text-sm">
            {tPlural(strings.missesLeft, locale, left, { count: fmt(left) })}
            {wrong.length > 0
              ? ` · ${strings.wrongLetters} ${wrong.join(" ")}`
              : ""}
          </p>
          {round.hintShown && round.clue ? (
            <p className="rounded-lg border border-border bg-muted/40 p-3 text-foreground text-sm">
              <span className="font-medium">{strings.clueLabel}</span>{" "}
              {round.clue}
            </p>
          ) : null}
          <p
            aria-live="polite"
            className="min-h-[1.5rem] font-medium text-foreground text-sm"
          >
            {message}
          </p>
        </div>
      </div>

      <fieldset
        aria-label={strings.keyboardLabel}
        className="m-0 flex min-w-0 flex-wrap justify-center gap-1.5 border-0 p-0"
      >
        {keys.map((letter) => (
          <button
            aria-label={keyLabel(strings, letter, round)}
            className={`h-11 w-11 rounded-md border font-semibold text-lg transition-colors disabled:cursor-not-allowed sm:h-12 sm:w-12 ${keyTone(round, letter)}`}
            disabled={over || round.guessed.includes(letter)}
            key={letter}
            onClick={() => onGuess(letter)}
            type="button"
          >
            {letter}
          </button>
        ))}
      </fieldset>

      <div className="flex flex-wrap justify-center gap-3">
        {round.clue && !round.hintShown && !over ? (
          <button className={BUTTON_SECONDARY} onClick={onClue} type="button">
            {strings.showClue}
          </button>
        ) : null}
        {canReveal && !over ? (
          <button className={BUTTON_SECONDARY} onClick={onReveal} type="button">
            {strings.revealLetter}
          </button>
        ) : null}
        {over ? (
          <button className={BUTTON_PRIMARY} onClick={onNext} type="button">
            {isLast ? strings.finish : strings.nextWord}
          </button>
        ) : null}
      </div>
      <p className={`${HINT} text-center`}>{strings.keyboardHint}</p>
    </div>
  );
}
